import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Legend, LineChart, Line,
} from 'recharts';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function CashFlowPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/reports/cashflow', { params: { months } });
      setData(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [months]);

  const totals = data.reduce((acc, row) => ({
    revenue: acc.revenue + row.revenue,
    expenses: acc.expenses + row.expenses,
    profit: acc.profit + row.profit,
  }), { revenue: 0, expenses: 0, profit: 0 });

  const chartData = data.map(r => ({
    month: r.month.slice(5), // MM
    Revenue: r.revenue,
    Expenses: r.expenses,
    Profit: r.profit,
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Cash Flow Report</Typography>
        <TextField select size="small" value={months} onChange={e => setMonths(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value={3}>Last 3 months</MenuItem>
          <MenuItem value={6}>Last 6 months</MenuItem>
          <MenuItem value={12}>Last 12 months</MenuItem>
        </TextField>
      </Box>

      {loading ? <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box> : (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary">Total Revenue</Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">₨ {fmt(totals.revenue)}</Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: 40, color: 'success.light' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary">Total Expenses</Typography>
                      <Typography variant="h4" fontWeight={700} color="error.main">₨ {fmt(totals.expenses)}</Typography>
                    </Box>
                    <TrendingDown sx={{ fontSize: 40, color: 'error.light' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ border: '2px solid', borderColor: totals.profit >= 0 ? 'success.main' : 'error.main' }}>
                <CardContent>
                  <Typography color="text.secondary">Net Profit</Typography>
                  <Typography variant="h4" fontWeight={700} color={totals.profit >= 0 ? 'success.main' : 'error.main'}>
                    {totals.profit >= 0 ? '+' : ''}₨ {fmt(totals.profit)}
                  </Typography>
                  <Chip
                    label={totals.revenue > 0 ? `${((totals.profit / totals.revenue) * 100).toFixed(1)}% margin` : '—'}
                    size="small" color={totals.profit >= 0 ? 'success' : 'error'} sx={{ mt: 0.5 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bar chart */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Revenue vs Expenses by Month</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Expenses" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Profit line chart */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Profit Trend</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Line type="monotone" dataKey="Profit" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Monthly table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Monthly Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Expenses</TableCell>
                    <TableCell align="right">Net Profit</TableCell>
                    <TableCell align="right">Margin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map(row => (
                    <TableRow key={row.month} hover>
                      <TableCell><Typography fontWeight={600}>{row.month}</Typography></TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>₨ {fmt(row.revenue)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>₨ {fmt(row.expenses)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={row.profit >= 0 ? 'success.main' : 'error.main'}>
                          {row.profit >= 0 ? '+' : ''}₨ {fmt(row.profit)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.revenue > 0 ? `${((row.profit / row.revenue) * 100).toFixed(1)}%` : '0%'}
                          size="small"
                          color={row.profit >= 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data for this period</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
